import { useMemo, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LifebuoyIcon,
  TrendUp,
  TrendDown,
  Clock,
  WarningCircle,
  Building,
  ArrowUp,
  ArrowDown,
  ChartLineUp,
  X,
  ArrowSquareOut,
  Plus,
  CheckCircle,
} from "@phosphor-icons/react";
import {
  DashboardBlock,
  DashboardBlockHeader,
  DashboardBlockContent,
} from "../layout/DashboardBlock";
import { BlockLoadingState } from "../states/BlockStates";
import {
  useBuildingBacklogEvolution,
  useStatisticsData,
} from "../../../helpdesk/statistics/hooks/useStatisticsData";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line, getElementAtEvent } from "react-chartjs-2";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  Filler,
);

interface SelectedPeriodData {
  periodLabel: string;
  period: string;
  created: number;
  completed: number;
  createdTicketIds: number[];
  completedTicketIds: number[];
}

interface HelpdeskMetricsBlockNewProps {
  isLoading?: boolean;
}

export const HelpdeskMetricsBlockNew = ({
  isLoading = false,
}: HelpdeskMetricsBlockNewProps) => {
  const navigate = useNavigate();
  const chartRef = useRef<any>(null);
  const [selectedPeriod, setSelectedPeriod] =
    useState<SelectedPeriodData | null>(null);

  // Fetch building backlog evolution for rising/declining buildings
  const { data: backlogData, loading: backlogLoading } =
    useBuildingBacklogEvolution("week", "Huurder");

  // Fetch time series data for ticket volume chart
  const { data: statisticsData, loading: statisticsLoadingState } =
    useStatisticsData({ granularity: "week" });
  const statisticsLoading = statisticsLoadingState.timeSeries;

  // Navigate to helpdesk with building filter
  const navigateToBuilding = (buildingId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/helpdesk?buildingId=${buildingId}`);
  };

  // Prepare chart data for ticket volume (Nieuw vs Opgelost)
  const chartData = useMemo(() => {
    if (
      !statisticsData?.timeSeries?.data ||
      statisticsData.timeSeries.data.length === 0
    )
      return null;

    return {
      labels: statisticsData.timeSeries.data.map((d) => d.periodLabel),
      datasets: [
        {
          label: "Nieuw",
          data: statisticsData.timeSeries.data.map((d) => d.created),
          borderColor: "#EA8162",
          backgroundColor: "rgba(234, 129, 98, 0.1)",
          fill: true,
          tension: 0.3,
          pointRadius: 3,
          pointHoverRadius: 6,
          pointBackgroundColor: "#EA8162",
          pointBorderColor: "#fff",
          pointBorderWidth: 2,
          borderWidth: 2,
        },
        {
          label: "Opgelost",
          data: statisticsData.timeSeries.data.map((d) => d.completed),
          borderColor: "#5B7304",
          backgroundColor: "rgba(91, 115, 4, 0.1)",
          fill: true,
          tension: 0.3,
          pointRadius: 3,
          pointHoverRadius: 6,
          pointBackgroundColor: "#5B7304",
          pointBorderColor: "#fff",
          pointBorderWidth: 2,
          borderWidth: 2,
        },
      ],
    };
  }, [statisticsData]);

  // Handle chart click to show period details
  const handleChartClick = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement>) => {
      if (!chartRef.current || !statisticsData?.timeSeries?.data) return;

      const elements = getElementAtEvent(chartRef.current, event);
      if (elements.length > 0) {
        const index = elements[0].index;
        const dataPoint = statisticsData.timeSeries.data[index];

        setSelectedPeriod({
          periodLabel: dataPoint.periodLabel,
          period: dataPoint.period,
          created: dataPoint.created,
          completed: dataPoint.completed,
          createdTicketIds: dataPoint.createdTicketIds || [],
          completedTicketIds: dataPoint.completedTicketIds || [],
        });
      }
    },
    [statisticsData],
  );

  // Open ticket in new tab
  const openTicketInNewTab = (ticketId: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(`/helpdesk/${ticketId}`, "_blank");
  };

  // Open tickets in helpdesk view
  const openTicketsInHelpdeskView = (
    ticketIds: number[],
    e: React.MouseEvent,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (ticketIds.length === 0) return;
    const ticketIdsParam = ticketIds.join(",");
    navigate(`/helpdesk?ticketIds=${ticketIdsParam}`);
  };

  // Chart options
  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: "index" as const,
        intersect: false,
      },
      plugins: {
        legend: {
          position: "bottom" as const,
          labels: {
            usePointStyle: true,
            padding: 8,
            boxWidth: 6,
            font: {
              size: 10,
            },
          },
        },
        tooltip: {
          backgroundColor: "#31403B",
          titleColor: "#F9F0EE",
          bodyColor: "#F9F0EE",
          borderColor: "#EA8162",
          borderWidth: 1,
          padding: 8,
          titleFont: {
            size: 11,
          },
          bodyFont: {
            size: 10,
          },
          callbacks: {
            afterBody: (context: any) => {
              const idx = context[0]?.dataIndex;
              if (idx !== undefined && statisticsData?.timeSeries?.data[idx]) {
                const point = statisticsData.timeSeries.data[idx];
                const netChange = point.created - point.completed;
                return [
                  "",
                  `Netto: ${netChange > 0 ? "+" : ""}${netChange}`,
                  "",
                  "Klik voor details",
                ];
              }
              return [];
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            font: {
              size: 9,
            },
            maxRotation: 0,
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            color: "rgba(49, 64, 59, 0.1)",
          },
          ticks: {
            font: {
              size: 9,
            },
            stepSize: 5,
          },
        },
      },
    }),
    [statisticsData],
  );

  return (
    <DashboardBlock>
      <DashboardBlockHeader
        icon={<LifebuoyIcon weight="duotone" size={20} />}
        title="Helpdesktickets"
        action={
          <Link
            to="/helpdesk/statistics"
            className="text-moss-primary text-xs hover:underline"
          >
            Statistieken
          </Link>
        }
      />
      <Link to="/helpdesk">
        <DashboardBlockContent interactive padding="md">
          {isLoading ? (
            <BlockLoadingState message="Helpdesk gegevens aan het ophalen..." />
          ) : (
            <div className="space-y-4">
              {/* Ticket Volume Chart */}
              {!statisticsLoading && statisticsData?.timeSeries && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-neutral-secondary-mid flex items-center gap-2 text-xs">
                      <ChartLineUp weight="duotone" size={14} />
                      <span>Ticket Volume</span>
                    </div>
                    <div className="text-neutral-secondary-mid flex items-center gap-3 text-xs">
                      <span>
                        <span className="font-semibold text-[#EA8162]">
                          {statisticsData.timeSeries.totals?.created ?? 0}
                        </span>{" "}
                        nieuw
                      </span>
                      <span>
                        <span className="font-semibold text-[#5B7304]">
                          {statisticsData.timeSeries.totals?.completed ?? 0}
                        </span>{" "}
                        opgelost
                      </span>
                    </div>
                  </div>

                  {/* Line Chart - Ticket Volume (Nieuw vs Opgelost) */}
                  {chartData && (
                    <div className="h-[180px] w-full cursor-pointer">
                      <Line
                        ref={chartRef}
                        data={chartData}
                        options={chartOptions}
                        onClick={handleChartClick}
                      />
                    </div>
                  )}

                  {/* Selected Period Detail Panel */}
                  {selectedPeriod && (
                    <div className="border-neutral-primary/10 rounded-lg border bg-neutral-50 p-3">
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-xs font-medium">
                          Details voor {selectedPeriod.periodLabel}
                        </span>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSelectedPeriod(null);
                          }}
                          className="rounded p-1 hover:bg-neutral-200"
                        >
                          <X size={12} />
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        {/* New Tickets */}
                        <div className="space-y-1">
                          <div className="flex items-center gap-1.5">
                            <div className="h-2 w-2 rounded-full bg-[#EA8162]" />
                            <span className="text-xs font-medium">
                              {selectedPeriod.created} nieuw
                            </span>
                            <Plus size={10} className="text-[#EA8162]" />
                          </div>
                          {selectedPeriod.createdTicketIds.length > 0 ? (
                            <div className="max-h-[80px] space-y-0.5 overflow-y-auto">
                              {selectedPeriod.createdTicketIds
                                .slice(0, 5)
                                .map((id) => (
                                  <button
                                    key={id}
                                    onClick={(e) => openTicketInNewTab(id, e)}
                                    className="text-moss-primary flex w-full items-center gap-1 text-left text-xs hover:underline"
                                  >
                                    <ArrowSquareOut size={10} />H{id}
                                  </button>
                                ))}
                              {selectedPeriod.createdTicketIds.length > 5 && (
                                <button
                                  onClick={(e) =>
                                    openTicketsInHelpdeskView(
                                      selectedPeriod.createdTicketIds,
                                      e,
                                    )
                                  }
                                  className="text-moss-primary text-xs hover:underline"
                                >
                                  +{selectedPeriod.createdTicketIds.length - 5}{" "}
                                  meer
                                </button>
                              )}
                            </div>
                          ) : (
                            <p className="text-neutral-secondary-mid text-xs">
                              Geen tickets
                            </p>
                          )}
                        </div>

                        {/* Completed Tickets */}
                        <div className="space-y-1">
                          <div className="flex items-center gap-1.5">
                            <div className="h-2 w-2 rounded-full bg-[#5B7304]" />
                            <span className="text-xs font-medium">
                              {selectedPeriod.completed} opgelost
                            </span>
                            <CheckCircle size={10} className="text-[#5B7304]" />
                          </div>
                          {selectedPeriod.completedTicketIds.length > 0 ? (
                            <div className="max-h-[80px] space-y-0.5 overflow-y-auto">
                              {selectedPeriod.completedTicketIds
                                .slice(0, 5)
                                .map((id) => (
                                  <button
                                    key={id}
                                    onClick={(e) => openTicketInNewTab(id, e)}
                                    className="text-moss-primary flex w-full items-center gap-1 text-left text-xs hover:underline"
                                  >
                                    <ArrowSquareOut size={10} />H{id}
                                  </button>
                                ))}
                              {selectedPeriod.completedTicketIds.length > 5 && (
                                <button
                                  onClick={(e) =>
                                    openTicketsInHelpdeskView(
                                      selectedPeriod.completedTicketIds,
                                      e,
                                    )
                                  }
                                  className="text-moss-primary text-xs hover:underline"
                                >
                                  +
                                  {selectedPeriod.completedTicketIds.length - 5}{" "}
                                  meer
                                </button>
                              )}
                            </div>
                          ) : (
                            <p className="text-neutral-secondary-mid text-xs">
                              Geen tickets
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Net Change */}
                      <div className="border-neutral-primary/10 mt-2 flex items-center justify-between border-t pt-2 text-xs">
                        <span className="text-neutral-secondary-mid">
                          Netto wijziging:
                        </span>
                        <span
                          className={`font-semibold ${
                            selectedPeriod.created - selectedPeriod.completed >
                            0
                              ? "text-red-500"
                              : selectedPeriod.created -
                                    selectedPeriod.completed <
                                  0
                                ? "text-green-500"
                                : "text-neutral-500"
                          }`}
                        >
                          {selectedPeriod.created - selectedPeriod.completed > 0
                            ? "+"
                            : ""}
                          {selectedPeriod.created - selectedPeriod.completed}{" "}
                          tickets
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Building Trends - Rising/Declining */}
              {!backlogLoading && backlogData && (
                <div className="space-y-3 pt-3">
                  <div className="flex items-center justify-between">
                    <div className="text-neutral-secondary-mid flex items-center gap-2 text-xs">
                      <Building weight="duotone" size={14} />
                      <span>Gebouw trends</span>
                    </div>
                    <Link
                      to="/helpdesk/statistics"
                      className="text-moss-primary text-xs hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Meer details
                    </Link>
                  </div>

                  {/* Rising Buildings */}
                  {backlogData.risingBuildings &&
                    backlogData.risingBuildings.length > 0 && (
                      <div>
                        <div className="mb-2 flex items-center gap-1.5">
                          <TrendUp
                            weight="bold"
                            className="text-red-500"
                            size={14}
                          />
                          <span className="text-xs font-medium text-red-600">
                            Stijgende tickets
                          </span>
                        </div>
                        <div className="space-y-1">
                          {backlogData.risingBuildings
                            .slice(0, 3)
                            .map((building) => (
                              <button
                                key={building.buildingId}
                                onClick={(e) =>
                                  navigateToBuilding(building.buildingId, e)
                                }
                                className="flex w-full items-center justify-between rounded-md bg-red-50 p-2 text-left transition-colors hover:bg-red-100"
                              >
                                <div className="flex min-w-0 items-center gap-2">
                                  <Building
                                    weight="duotone"
                                    size={14}
                                    className="shrink-0 text-red-600"
                                  />
                                  <span className="max-w-[140px] truncate text-xs">
                                    {building.buildingName}
                                  </span>
                                </div>
                                <div className="flex shrink-0 items-center gap-1.5">
                                  <span className="text-neutral-secondary-mid text-xs">
                                    {building.previousOpenCount} →{" "}
                                    {building.currentOpenCount}
                                  </span>
                                  <span className="inline-flex items-center gap-0.5 rounded bg-red-500 px-1.5 py-0.5 text-xs font-medium text-white">
                                    <ArrowUp size={10} />+
                                    {building.absoluteChange}
                                  </span>
                                </div>
                              </button>
                            ))}
                          {backlogData.risingBuildings.length > 3 && (
                            <Link
                              to="/helpdesk/statistics"
                              className="block pl-2 text-xs text-red-600 hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              +{backlogData.risingBuildings.length - 3} meer...
                            </Link>
                          )}
                        </div>
                      </div>
                    )}

                  {/* Declining Buildings */}
                  {backlogData.decliningBuildings &&
                    backlogData.decliningBuildings.length > 0 && (
                      <div>
                        <div className="mb-2 flex items-center gap-1.5">
                          <TrendDown
                            weight="bold"
                            className="text-green-500"
                            size={14}
                          />
                          <span className="text-xs font-medium text-green-600">
                            Dalende tickets
                          </span>
                        </div>
                        <div className="space-y-1">
                          {backlogData.decliningBuildings
                            .slice(0, 3)
                            .map((building) => (
                              <button
                                key={building.buildingId}
                                onClick={(e) =>
                                  navigateToBuilding(building.buildingId, e)
                                }
                                className="flex w-full items-center justify-between rounded-md bg-green-50 p-2 text-left transition-colors hover:bg-green-100"
                              >
                                <div className="flex min-w-0 items-center gap-2">
                                  <Building
                                    weight="duotone"
                                    size={14}
                                    className="shrink-0 text-green-600"
                                  />
                                  <span className="max-w-[140px] truncate text-xs">
                                    {building.buildingName}
                                  </span>
                                </div>
                                <div className="flex shrink-0 items-center gap-1.5">
                                  <span className="text-neutral-secondary-mid text-xs">
                                    {building.previousOpenCount} →{" "}
                                    {building.currentOpenCount}
                                  </span>
                                  <span className="inline-flex items-center gap-0.5 rounded bg-green-500 px-1.5 py-0.5 text-xs font-medium text-white">
                                    <ArrowDown size={10} />
                                    {building.absoluteChange}
                                  </span>
                                </div>
                              </button>
                            ))}
                          {backlogData.decliningBuildings.length > 3 && (
                            <Link
                              to="/helpdesk/statistics"
                              className="block pl-2 text-xs text-green-600 hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              +{backlogData.decliningBuildings.length - 3}{" "}
                              meer...
                            </Link>
                          )}
                        </div>
                      </div>
                    )}

                  {/* No trends message */}
                  {(!backlogData.risingBuildings ||
                    backlogData.risingBuildings.length === 0) &&
                    (!backlogData.decliningBuildings ||
                      backlogData.decliningBuildings.length === 0) && (
                      <div className="text-neutral-secondary-mid py-2 text-center text-xs">
                        Geen significante gebouw trends deze week
                      </div>
                    )}
                </div>
              )}

              {/* SLA Alerts at Bottom */}
              {!statisticsLoadingState.actionItems && statisticsData?.actionItems && (
                ((statisticsData.actionItems.responseTimeAtRisk?.length ?? 0) > 0 ||
                 (statisticsData.actionItems.slaAtRisk?.length ?? 0) > 0) && (
                  <div className="border-neutral-primary/10 space-y-3 border-t pt-4">
                    {/* Reactietijd SLA */}
                    {(statisticsData.actionItems.responseTimeAtRisk?.length ?? 0) > 0 && (
                      <div className="rounded-lg bg-amber-50 p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <WarningCircle weight="fill" size={16} className="text-amber-600" />
                            <span className="text-sm font-medium text-amber-700">Reactietijd SLA</span>
                          </div>
                          <span className="text-lg font-bold text-amber-700">
                            {statisticsData.actionItems.responseTimeAtRisk.length}
                          </span>
                        </div>
                        <p className="text-xs text-amber-600 mb-2">Tickets zonder reactie binnen 72 uur</p>
                        <div className="space-y-1">
                          {statisticsData.actionItems.responseTimeAtRisk.slice(0, 5).map((ticket) => (
                            <button
                              key={ticket.id}
                              onClick={(e) => openTicketInNewTab(ticket.id, e)}
                              className="flex w-full items-center justify-between rounded bg-amber-100/50 px-2 py-1.5 text-left transition-colors hover:bg-amber-100"
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="text-xs font-semibold text-amber-700">H{ticket.id}</span>
                                <span className="text-xs text-amber-700 truncate max-w-[150px]">{ticket.title}</span>
                              </div>
                              <span className="text-xs text-amber-600 shrink-0">{ticket.hoursWithoutResponse}u</span>
                            </button>
                          ))}
                          {statisticsData.actionItems.responseTimeAtRisk.length > 5 && (
                            <Link
                              to="/helpdesk/statistics"
                              className="block text-xs text-amber-700 hover:underline pt-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              +{statisticsData.actionItems.responseTimeAtRisk.length - 5} meer bekijken...
                            </Link>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Doorlooptijd SLA Risico */}
                    {(statisticsData.actionItems.slaAtRisk?.length ?? 0) > 0 && (
                      <div className="rounded-lg bg-red-50 p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Clock weight="fill" size={16} className="text-red-600" />
                            <span className="text-sm font-medium text-red-700">Doorlooptijd SLA Risico</span>
                          </div>
                          <span className="text-lg font-bold text-red-700">
                            {statisticsData.actionItems.slaAtRisk.length}
                          </span>
                        </div>
                        <p className="text-xs text-red-600 mb-2">Tickets die SLA deadline naderen</p>
                        <div className="space-y-1">
                          {statisticsData.actionItems.slaAtRisk.slice(0, 5).map((ticket) => (
                            <button
                              key={ticket.id}
                              onClick={(e) => openTicketInNewTab(ticket.id, e)}
                              className="flex w-full items-center justify-between rounded bg-red-100/50 px-2 py-1.5 text-left transition-colors hover:bg-red-100"
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="text-xs font-semibold text-red-700">H{ticket.id}</span>
                                <span className="text-xs text-red-700 truncate max-w-[150px]">{ticket.title}</span>
                              </div>
                              <span className="text-xs text-red-600 shrink-0">
                                {ticket.daysRemaining > 0 ? `${ticket.daysRemaining}d over` : 'Verlopen'}
                              </span>
                            </button>
                          ))}
                          {statisticsData.actionItems.slaAtRisk.length > 5 && (
                            <Link
                              to="/helpdesk/statistics"
                              className="block text-xs text-red-700 hover:underline pt-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              +{statisticsData.actionItems.slaAtRisk.length - 5} meer bekijken...
                            </Link>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )
              )}
            </div>
          )}
        </DashboardBlockContent>
      </Link>
    </DashboardBlock>
  );
};
